import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useConfig } from "../context/ConfigContext";
import { toast } from "sonner";
import { UserPlus, Trash2, Clock } from "lucide-react";

const BLANK = { name: "", contact: "", source: "", interest_level: "Warm", status: "New", party_size: 1, next_follow_up: "", deposited: false, notes: "" };

export default function Leads() {
  const { config } = useConfig();
  const [leads, setLeads] = useState([]);
  const [due, setDue] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [show, setShow] = useState(false);

  const load = () => { api.leads().then(setLeads); api.leadsDue().then(setDue); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    await api.createLead({ ...form, party_size: Number(form.party_size) || 1, next_follow_up: form.next_follow_up || null });
    toast.success("Lead added"); setForm(BLANK); setShow(false); load();
  };
  const patch = (id, b) => api.updateLead(id, b).then(load);
  const statuses = config?.lead_statuses || ["New", "Interested", "Deposited", "Closed"];

  const Row = ({ l }) => (
    <tr className="border-b border-[#2A3042]/60" data-testid={`lead-row-${l.id}`}>
      <td className="py-3 pr-3">
        <div className="text-[#F8FAFC]">{l.name}</div>
        <div className="text-xs text-[#64748B]">{l.contact} · party {l.party_size}</div>
      </td>
      <td className="pr-3">
        <select value={l.status} onChange={(e) => patch(l.id, { status: e.target.value })} data-testid={`lead-status-${l.id}`}
          className="px-2 py-1.5 rounded bg-[#0A0C10] border border-[#2A3042] text-xs outline-none focus:border-[#D4AF37]">
          {statuses.map((s) => <option key={s}>{s}</option>)}
        </select>
      </td>
      <td className="pr-3">
        <input type="date" value={l.next_follow_up || ""} onChange={(e) => patch(l.id, { next_follow_up: e.target.value })}
          className="px-2 py-1.5 rounded bg-[#0A0C10] border border-[#2A3042] text-xs outline-none focus:border-[#D4AF37]" />
      </td>
      <td className="pr-3 text-center">
        <input type="checkbox" checked={l.deposited} onChange={(e) => patch(l.id, { deposited: e.target.checked })}
          data-testid={`lead-deposited-${l.id}`} className="accent-[#D4AF37] w-4 h-4" />
      </td>
      <td className="text-right">
        <button data-testid={`del-lead-${l.id}`} onClick={() => api.delLead(l.id).then(load)} className="text-[#64748B] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
      </td>
    </tr>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow text-[#D4AF37]">Follow-Up Director</div>
          <h1 className="font-display text-3xl sm:text-4xl mt-1">Who should I follow up with?</h1>
        </div>
        <button data-testid="add-lead-btn" onClick={() => setShow((s) => !s)} className="gold-btn px-4 py-2.5 rounded-lg text-sm inline-flex items-center gap-2"><UserPlus className="w-4 h-4" /> Add lead</button>
      </div>

      {show && (
        <div className="card-lux p-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="lead-form">
          {[["name", "Name"], ["contact", "Contact (phone/email)"], ["source", "Source"], ["notes", "Notes"]].map(([k, ph]) => (
            <input key={k} placeholder={ph} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              data-testid={`lead-input-${k}`} className="px-3 py-2.5 rounded-lg bg-[#0A0C10] border border-[#2A3042] text-sm outline-none focus:border-[#D4AF37]" />
          ))}
          <input type="number" min="1" placeholder="Party size" value={form.party_size} onChange={(e) => setForm({ ...form, party_size: e.target.value })}
            className="px-3 py-2.5 rounded-lg bg-[#0A0C10] border border-[#2A3042] text-sm outline-none focus:border-[#D4AF37]" />
          <input type="date" value={form.next_follow_up} onChange={(e) => setForm({ ...form, next_follow_up: e.target.value })}
            className="px-3 py-2.5 rounded-lg bg-[#0A0C10] border border-[#2A3042] text-sm outline-none focus:border-[#D4AF37]" />
          <button data-testid="save-lead-btn" onClick={save} className="gold-btn rounded-lg text-sm py-2.5">Save lead</button>
        </div>
      )}

      {due.length > 0 && (
        <div className="card-lux p-5 border-[#F59E0B]/40">
          <div className="flex items-center gap-2 eyebrow text-[#F59E0B] mb-2"><Clock className="w-4 h-4" /> Due now ({due.length})</div>
          <div className="flex flex-wrap gap-2">
            {due.map((l) => <span key={l.id} className="text-sm px-3 py-1 rounded-full bg-[#222736] text-[#F8FAFC]">{l.name}</span>)}
          </div>
        </div>
      )}

      <div className="card-lux p-5 overflow-x-auto">
        {leads.length === 0 ? <p className="text-sm text-[#64748B]">No leads yet. Add warm prospects to track follow-ups and deposits.</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-[#64748B] uppercase tracking-wider border-b border-[#2A3042]">
              <th className="py-2 pr-3">Lead</th><th className="pr-3">Status</th><th className="pr-3">Next follow-up</th><th className="pr-3">Deposited</th><th></th>
            </tr></thead>
            <tbody>{leads.map((l) => <Row key={l.id} l={l} />)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
